import {
  CompareDataset,
  DatasetRef,
  datasetToCompareItem,
  isSameDatasetRef,
  toReleaseRef,
  useCompare,
} from 'components/shared/Compare';
import useDataset from 'hooks/data/datasets/useDataset';

/**
 * Resolves a DatasetRef to a CompareDataset: prefers the rich item already staged in the compare
 * context, and falls back to fetching by id (react-query caches, so calling this for the same ref from
 * several components triggers only one request).
 *
 * Takes a full ref rather than an id on purpose. An id-only lookup would match a staged dataset from a
 * *different* release, rendering that release's name and geography beside this release's numbers.
 *
 * V4 releases are rejected when the URL is parsed and can't be selected through the UI, so only the
 * 3.x endpoint is wired here.
 */
export const useResolveCompareDataset = (ref?: DatasetRef): { dataset?: CompareDataset; isLoading: boolean } => {
  const { datasets } = useCompare();

  const contextItem = ref ? datasets.find((dataset) => isSameDatasetRef(dataset, ref)) : undefined;
  const shouldFetch = Boolean(ref) && !contextItem;
  const release = ref ? toReleaseRef(ref) : undefined;

  const { data: fetched, isLoading } = useDataset(shouldFetch ? ref?.id : undefined, false, release);

  const dataset =
    contextItem ??
    // The fallback release is the one we requested, never the route's: stamping the route here would
    // send every later fetch for this dataset to a release where its id means something else.
    (ref && fetched ? datasetToCompareItem(ref.id, fetched, { ver: ref.version, system_model: ref.systemModel }) : undefined);

  return { dataset, isLoading: shouldFetch && isLoading };
};
